/**
 * Consumer product-LINE browse view for the register screen (step 0).
 *
 * A tenant selling one product in several sizes carries a `VARIANT_BASE` per
 * line and a `VARIANT` per size, so the flat layouts list every size as its own
 * row - 47 lines become 148 near-identical rows, which is how a consumer picks
 * the wrong size. This view lists the LINES, each a collapsible row that
 * lazy-loads its sizes on expand.
 *
 * A `VARIANT_BASE` is not registrable, so a line row only ever expands; the
 * size the consumer picks inside it is what advances the wizard. Rows that are
 * already registrable (a standalone `PRODUCT`, or a published size whose line
 * is unpublished) select directly.
 *
 * Offered only when the tenant asks for it, via
 * `Tenant.settings.consumer.product_picker` - a flat catalogue has no lines to
 * group by. Generic: no tenant.slug branches.
 */
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ChevronDown, ChevronRight, Package } from 'lucide-react'

import { Button } from '@/shared/components/ui/button'
import { Image } from '@/shared/components/ui/image'
import { PaginationControls } from '@/admin/components/paginated-table'
import {
  useConsumerProductLines,
  useConsumerProductVariants,
} from '@/consumer/hooks/use-registration'
import type { ProductSearchResult } from '@/consumer/hooks/use-registration'

// ── The sizes under one line, loaded when the row is expanded ──────────────

function LineVariantRows({
  line,
  onSelect,
}: {
  line: ProductSearchResult
  onSelect: (product: ProductSearchResult) => void
}) {
  const { t } = useTranslation()
  const { data, isPending, isError } = useConsumerProductVariants(line.uuid)
  const variants = data?.results ?? []

  // Loading first: a cached-empty result would otherwise flash "no sizes".
  if (isPending) {
    return (
      <p className="py-2.5 pl-[46px] pr-3 text-[11px] text-[var(--ink-3)]">
        {t('register.loadingSizes', { defaultValue: 'Loading sizes...' })}
      </p>
    )
  }

  if (isError) {
    return (
      <p className="py-2.5 pl-[46px] pr-3 text-[11px] text-[var(--status-error)]">
        {t('common.loadError', {
          defaultValue: "Couldn't load this list. Please try again.",
        })}
      </p>
    )
  }

  if (variants.length === 0) {
    return (
      <p className="py-2.5 pl-[46px] pr-3 text-[11px] text-[var(--ink-3)]">
        {t('register.noSizesInLine', {
          defaultValue: 'No sizes available in this range',
        })}
      </p>
    )
  }

  return (
    <div className="flex flex-col">
      {variants.map((v) => (
        <Button
          key={v.uuid}
          type="button"
          variant="ghost"
          data-testid={`consumer-product-item-${v.uuid}`}
          onClick={() => onSelect(v)}
          className="flex h-auto w-full cursor-pointer items-center justify-start gap-3 rounded-none py-2.5 pl-[46px] pr-3 text-left hover:bg-[var(--bg-subtle)]"
        >
          <span className="text-[13px] font-normal text-[var(--ink)]">
            {v.cached_short_name || v.cached_long_name}
          </span>
        </Button>
      ))}
    </div>
  )
}

// ── One line row ───────────────────────────────────────────────────────────

function LineRow({
  line,
  onSelect,
}: {
  line: ProductSearchResult
  onSelect: (product: ProductSearchResult) => void
}) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)

  // A line is only a container when it actually has sizes under it. Anything
  // else on this feed - a standalone PRODUCT, or a published size whose line is
  // unpublished - is registrable and selects straight through.
  const isLine = line.product_type === 'VARIANT_BASE' && (line.variant_count ?? 0) > 0
  const count = line.variant_count ?? 0
  // A VARIANT_BASE is never registrable, so it must never be handed to
  // onSelect - a zero-size base would otherwise fall through the isLine check
  // and put an unregistrable product into the wizard. The endpoint already
  // drops those, and this is the second lock: the rule is a property of the
  // product type, not of one query's filters.
  const isSelectable = line.product_type !== 'VARIANT_BASE'

  return (
    <div className="border-b-[0.5px] border-b-[var(--rule)] last:border-b-0">
      <Button
        type="button"
        variant="ghost"
        aria-expanded={isLine ? open : undefined}
        disabled={!isLine && !isSelectable}
        data-testid={
          isLine ? `consumer-product-line-${line.uuid}` : `consumer-product-item-${line.uuid}`
        }
        onClick={() => (isLine ? setOpen((v) => !v) : isSelectable ? onSelect(line) : undefined)}
        className="flex h-auto w-full cursor-pointer items-center justify-start gap-3 rounded-none p-3 text-left hover:bg-[var(--bg-subtle)]"
      >
        {isLine ? (
          open ? (
            <ChevronDown className="h-4 w-4 shrink-0 text-[var(--ink-3)]" />
          ) : (
            <ChevronRight className="h-4 w-4 shrink-0 text-[var(--ink-3)]" />
          )
        ) : (
          <span className="h-4 w-4 shrink-0" />
        )}
        <Image
          src={line.image?.file_url}
          thumbnailSrc={line.image?.thumbnail_url}
          alt=""
          className="h-10 w-10 shrink-0 rounded-[4px] bg-[var(--bg-subtle)]"
          fallback={<Package className="h-4 w-4 text-[var(--ink-3)]" />}
        />
        <span className="flex min-w-0 flex-col">
          <span className="truncate text-sm font-normal text-[var(--ink)]">
            {line.cached_short_name || line.cached_long_name}
          </span>
          <span className="text-xs text-[var(--ink-3)]">
            {isLine
              ? count === 1
                ? t('register.oneSize', { defaultValue: '1 size' })
                : t('register.sizeCount', {
                    count,
                    defaultValue: '{{count}} sizes',
                  })
              : line.product_template.name}
          </span>
        </span>
      </Button>
      {isLine && open ? <LineVariantRows line={line} onSelect={onSelect} /> : null}
    </div>
  )
}

// ── The view ───────────────────────────────────────────────────────────────

export function ConsumerLinesView({
  onSelect,
}: {
  onSelect: (product: ProductSearchResult) => void
}) {
  const { t } = useTranslation()
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(20)
  const { data, isPending, isError } = useConsumerProductLines(page, pageSize)
  const lines = data?.results ?? []
  const totalCount = data?.count ?? 0

  if (isPending) {
    return (
      <p className="py-12 text-center text-sm text-[var(--ink-3)]">
        {t('register.loadingProducts', { defaultValue: 'Loading products...' })}
      </p>
    )
  }

  if (isError) {
    return (
      <p className="py-12 text-center text-sm text-[var(--status-error)]">
        {t('common.loadError', {
          defaultValue: "Couldn't load this list. Please try again.",
        })}
      </p>
    )
  }

  if (lines.length === 0) {
    return (
      <div className="py-12 text-center">
        <Package className="mx-auto mb-3 h-10 w-10 text-[var(--ink-3)] opacity-40" />
        <p className="text-sm text-[var(--ink-3)]">
          {t('register.noProductsAvailable', {
            defaultValue: 'No products available to register yet',
          })}
        </p>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-3">
      {/* Bounded and scrollable: a page of lines plus any expanded sizes must
          not push the wizard's controls off-screen. Paginated on top, so a
          long catalogue is a few short pages rather than one endless column. */}
      <div className="max-h-[70vh] overflow-y-auto overscroll-contain rounded-[8px] border-[0.5px] border-solid border-[var(--rule)] bg-[var(--bg)]">
        {lines.map((line) => (
          <LineRow key={line.uuid} line={line} onSelect={onSelect} />
        ))}
      </div>
      <PaginationControls
        page={page}
        pageSize={pageSize}
        totalCount={totalCount}
        onPageChange={setPage}
        onPageSizeChange={(size) => {
          setPageSize(size)
          setPage(1)
        }}
      />
    </div>
  )
}
